﻿import * as LogService from "./services/logservice/log.service";
import * as MessageBarService from './services/messageBarService/messageBar.service';

export interface IDataService<TModel> {
    get(config: angular.IRequestShortcutConfig, url?: string): ng.IPromise<any>;
    getById(id: number): ng.IPromise<any>;
    post(postData: TModel, config?: ng.IRequestShortcutConfig, url?: string): ng.IPromise<any>;
    put(postData: TModel, config?: ng.IRequestShortcutConfig, url?: string): ng.IPromise<any>;
    deleteById(id: string, config?: ng.IRequestShortcutConfig): ng.IPromise<any>;
}

export abstract class DataServiceBase<TModel> implements IDataService<TModel> {

    constructor(
        protected $q: angular.IQService,
        protected $http: angular.IHttpService,
        protected logger: LogService.ILogService,
        protected messageBarService: MessageBarService.MessageBarService,
        protected url: string) {
    }

    /**
     *  a default error handler that just logs the error
     *  use it from your implementation by passing it to the fail handler 
     *  returns a rejected promise that contains the error message
     *  uses text in error.data.message, so if you alter the text before calling this helper, you can manipulate the error message
    */
    protected errorHandler<TReturn>(error: ng.IHttpPromiseCallbackArg<TReturn>): ng.IPromise<string> {
        const message = `Dataservice Error: ${error.status}, Message: ${error.statusText}, Endpoint: ${error.config.url}`;
        this.logger.error(message, error);

        return this.$q.reject(error);
    }

    protected successHandler(response: ng.IHttpPromiseCallbackArg<any>): any {
        const message = `Dataservice successfully executed.  Endpoint: ${response.config.url}`;
        this.logger.debug(message, response);
        return this.transformInboundModel(response.data);
    }

    protected transformInboundModel(model: TModel): TModel {
        return model;
    }

    protected transformOutboundModel(model: TModel): TModel {
        return model;
    }

    /**
     * Convenience method for when you want to retrieve a model by its ID.
     * @param successCallback callback on success: should expect 1 instance of model to be passed as a parameter
     */
    public getById(id: string | number): ng.IPromise<any> {
        // leaving this commented line to prevent a future regression:  we used to use params to convey the id, but this results in a url with a query string, and web api attribute routing seems to have trouble with query strings
        // we intentionally switched to interpolating the id into the url
        //const config: ng.IRequestShortcutConfig = { params: { id: id } };

        const url = `${this.url}/${id}`;
        return this.$http.get(url)
            .then((response: ng.IHttpPromiseCallbackArg<TModel>) => this.successHandler(response))
            .catch((error: ng.IHttpPromiseCallbackArg<TModel>): angular.IPromise<any> => this.errorHandler(error));
    }

    /**
     * performs a get operation using $http.get(), with wrapping for success/fail logging.
     * @param successCallback callback function to call on success expect an array of model type
     * @param url optional:  use this to override the default url.  For example, if you have an action-based api to call.  It must be the full URL, not a fragment
     * @param config : the angular $http config object to pass to the $http service
     */
    public get(url?: string, config?: angular.IRequestShortcutConfig): ng.IPromise<any> {
        // typescript 1.8.4 doesn't seem to correctly detect when the caller accidentally passes a number into config (like if you meant to call getById())
        // It should be a compile time error, but for some reason, it's a runtime error
        url = url ? url : this.url;
        if (!isNaN(url as any)) {
            throw new Error(`Invalid parameters passed to DataServiceBase.get.  Did you intend to use GetById? configParameter: ${JSON.stringify(config)}`);
        }

        return this.$http.get(url, config)
            .then((response: ng.IHttpPromiseCallbackArg<TModel[]>) => this.successHandler(response))
            // note: you need to call this.errorHandler in a lambda, or else the definition of "this" within the error handler is wrong
            .catch((error: ng.IHttpPromiseCallbackArg<any>) => this.errorHandler(error));
    }

    /**
     * performs a Post operation and returns the ID that the called service returns
     * @param postData the model to be posted
     * @param successCallback callback function to call on success -- use this to retrieve the get result
     * @param url optional:  use this to override the default url.  For example, if you have an action-based api to call.  It must be the full URL, not a fragment
     * @param config : the angular $http config object to pass to the $http service
     */
    public post(postData: TModel, config?: ng.IRequestShortcutConfig, url?: string): ng.IPromise<any> {
        url = url ? url : this.url;
        postData = this.transformOutboundModel(postData);
        return this.$http.post(url, postData, config)
            .then((response: ng.IHttpPromiseCallbackArg<TModel>) => this.successHandler(response))
            .catch((error: ng.IHttpPromiseCallbackArg<any>) => this.errorHandler(error));
    }

    /**
     * performs a Put operation
     * @param postData the model to be PUT
     * @param successCallback callback function to call on success -- use this to retrieve the get result
     * @param url optional:  use this to override the default url.  For example, if you have an action-based api to call.  It must be the full URL, not a fragment
     * @param config : the angular $http config object to pass to the $http service
     */
    public put(postData: TModel, config?: ng.IRequestShortcutConfig, url?: string): ng.IPromise<any> {
        url = url ? url : this.url;
        postData = this.transformOutboundModel(postData);
        return this.$http.put(url, postData, config)
            .then((response: ng.IHttpPromiseCallbackArg<TModel>) => this.successHandler(response))
            .catch((error: ng.IHttpPromiseCallbackArg<any>) => this.errorHandler(error));
    }

    public deleteById(id: string, config?: ng.IRequestShortcutConfig): ng.IPromise<any> {
        const url = `${this.url}/${id}`;
        return this.$http.delete(url, config)
            .then((response: ng.IHttpPromiseCallbackArg<TModel>) => this.successHandler(response))
            .catch((error: ng.IHttpPromiseCallbackArg<TModel>): angular.IPromise<any> => this.errorHandler(error));
    }
}
